import { useState } from "react";

import type { TokenCollectorProps } from "../useTokenPalette";
import { CollectorFooter } from "./CollectorFooter";
import { TokenParamField } from "./TokenParamField";

/**
 * One generic collector for every parameterized token: it renders a {@link TokenParamField} per
 * `definition.params` (the widget chosen from each param's `kind`), gathers the values, and builds
 * the token on submit. No per-token collector components are needed.
 */
export const TokenCollector = ({ definition, onSubmit, onCancel, initialValues }: TokenCollectorProps) => {
  const [values, setValues] = useState<Record<string, string>>(() => initialValues ?? {});
  const params = definition.params ?? [];
  const canSubmit = params.every((param) => !param.required || Boolean(values[param.key]?.trim()));

  // The popover heading already shows the token label, so a param whose label repeats it (e.g. the
  // "Snippet" token's sole "Snippet" param) is redundant — let the field stand on its own.
  const headingLabel = definition.getLabel().trim().toLowerCase();

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        // The popover portals this form out of the DOM but it stays a React descendant of any outer
        // form, so React would bubble this submit to the outer form's onSubmit — stop it here.
        event.stopPropagation();
        if (canSubmit) onSubmit(values);
      }}
    >
      <div className="flex flex-col gap-3">
        {params.map((param) => (
          <TokenParamField
            key={param.key}
            param={param}
            dataSource={definition.dataSource}
            value={values[param.key] ?? ""}
            onChange={(value) => setValues((prev) => ({ ...prev, [param.key]: value }))}
            hideLabel={param.getLabel().trim().toLowerCase() === headingLabel}
          />
        ))}
      </div>
      <CollectorFooter canSubmit={canSubmit} onCancel={onCancel} />
    </form>
  );
};
